import 'client-only';

import { atom, useAtom } from 'jotai';
import { useEffect } from 'react';

import { STRIPE_KEY } from '@/consts/environmental';

const createStripeClient = () =>
  typeof window !== 'undefined' ? window.Stripe?.(STRIPE_KEY) : undefined;

const stripeClientAtom = atom<stripe.Stripe | undefined>(
  // If the Stripe script is already loaded, initialize the client immediately.
  createStripeClient(),
);

/*
 * A hook to load the Stripe client.
 *
 * This can be used in as many places as needed. It will only load the Stripe script once and all usages of the hook will share the same client.
 */
export const useStripeClient = () => {
  const [client, setClient] = useAtom(stripeClientAtom);

  useEffect(() => {
    if (document.getElementById('stripe-js')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'stripe-js';
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;

    script.onload = () => {
      const stripeClient = createStripeClient();

      if (stripeClient) {
        setClient(stripeClient);
      } else {
        console.error('Failed to initialize the Stripe client.');
      }
    };

    script.onerror = () => {
      console.error('Failed to load the Stripe script.');
    };

    document.body.appendChild(script);
  }, [setClient]);

  return client;
};
